// Based on Grafana datematch licensed under APACHE.
// https://github.com/grafana/grafana/blob/f75387bd146e6b0c91ede0f01203485f6964885b/packages/grafana-data/src/datetime/datemath.ts

import includes from "lodash/includes.js";
import isDate from "lodash/isDate.js";
import { OpUnitType, Dayjs } from "dayjs";
import dayjs from "dayjs";

const { isDayjs } = dayjs;

const units: OpUnitType[] = ["y", "M", "Q", "w", "d", "h", "m", "s"];

export function isMathString(text: string | Dayjs | Date): boolean {
	if (!text) {
		return false;
	}

	return (
		typeof text === "string" &&
		(text.substring(0, 3) === "now" || text.includes("||"))
	);
}

function dateTimeForTimeZone(timezone: string): Dayjs {
	if (timezone === "utc") {
		return dayjs().utc();
	}
	return dayjs();
}

/**
 * Parses different types input to a moment instance. There is a specific formatting language that can be used
 * if text arg is string. See unit tests for examples.
 * @param text
 * @param roundUp See parseDateMath function.
 * @param timezone Only string 'utc' is acceptable here, for anything else, local timezone is used.
 */
export function parse(
	text?: string | Dayjs | Date | null,
	roundUp?: boolean,
	timezone?: string
): Dayjs | undefined {
	if (!text) {
		return undefined;
	}

	if (typeof text !== "string") {
		if (isDayjs(text)) {
			return text;
		}
		if (isDate(text)) {
			return dayjs(text);
		}
		// We got some non string which is not a moment nor Date. TS should be able to check for that but not always.
		return undefined;
	}

	let time;
	let mathString;
	let index;
	let parseString;

	if (text.substring(0, 3) === "now") {
		time = dateTimeForTimeZone(timezone);
		mathString = text.substring("now".length);
	} else {
		index = text.indexOf("||");
		if (index === -1) {
			parseString = text;
			mathString = ""; // nothing else
		} else {
			parseString = text.substring(0, index);
			mathString = text.substring(index + 2);
		}

		time = dayjs(parseString);
	}

	if (!mathString.length) {
		return time;
	}

	return parseDateMath(mathString, time, roundUp);
}

/**
 * Checks if text is a valid date which in this context means that it is either a Moment instance or it can be parsed
 * by parse function. See parse function to see what is considered acceptable.
 * @param text
 */
export function isValid(text: string | Dayjs): boolean {
	const date = parse(text);
	if (!date) {
		return false;
	}

	if (isDayjs(date)) {
		return date.isValid();
	}

	return false;
}

/**
 * Parses math part of the time string and shifts supplied time according to that math. See unit tests for examples.
 * @param mathString
 * @param time
 * @param roundUp If true it will round the time to endOf time unit, otherwise to startOf time unit.
 */
// TODO: Had to revert Andrejs `time: moment.Moment` to `time: any`
export function parseDateMath(
	mathString: string,
	time: any,
	roundUp?: boolean
): Dayjs | undefined {
	enum Operator {
		ROUND,
		ADD,
		SUBTRACT,
	}
	const strippedMathString = mathString.replace(/\s/g, "");
	let dateTime = time;
	let i = 0;
	const len = strippedMathString.length;

	while (i < len) {
		const operator = strippedMathString.charAt(i++);
		let type: number;
		let num;
		let unit: string;

		switch (operator) {
			case "/":
				type = Operator.ROUND;
				break;
			case "+":
				type = Operator.ADD;
				break;
			case "-":
				type = Operator.SUBTRACT;
				break;
			default:
				return undefined;
		}

		if (isNaN(parseInt(strippedMathString.charAt(i), 10))) {
			num = 1;
		} else if (strippedMathString.length === 2) {
			num = strippedMathString.charAt(i);
		} else {
			const numFrom = i;
			while (!isNaN(parseInt(strippedMathString.charAt(i), 10))) {
				i++;
				// the number of units * characters per operation (3) is the longest dynamic date which makes sense
				if (i > units.length * 3) {
					return undefined;
				}
			}
			num = parseInt(strippedMathString.substring(numFrom, i), 10);
		}

		// rounding is only allowed on whole, single, units (eg M or 1M, not 0.5M or 2M)
		if (type === Operator.ROUND && num !== 1) {
			return undefined;
		}

		unit = strippedMathString.charAt(i++);
		if (!includes(units, unit)) {
			return undefined;
		}
		if (type === Operator.ROUND) {
			// w should mean isoWeek and not regular week
			if (unit === "w") {
				unit = "isoWeek";
			}
			if (unit === "Q") {
				unit = "quarters";
			}

			if (roundUp) {
				dateTime = dateTime.endOf(unit);
			} else {
				dateTime = dateTime.startOf(unit);
			}
		} else if (type === Operator.ADD) {
			dateTime = dateTime.add(num, unit);
		} else if (type === Operator.SUBTRACT) {
			dateTime = dateTime.subtract(num, unit);
		}
	}
	return dateTime;
}
